/*
 * Flagged Simulations page.
 *
 * Review queue for sims that trigger one of four flag criteria. Each
 * card shows the sim, its tags, the matched criteria, why it was
 * flagged and a remediation hint. Dismissals persist locally so the
 * admin can hide stale flags without retiring the sim itself.
 *
 * Calculations (see DIFFICULTY_THRESHOLDS / TAG_PENALTY below):
 *   - per-sim click rate = clicks / sent across SEND_LOG.
 *   - per-sim threshold = max(0.05, baseDifficulty - sum(tagPenalties)).
 *   - criteria:
 *       click-rate-exceeds-expected: clickRate > threshold.
 *       credential-page-reached: secondStage === 'credential_harvest' AND
 *           secondStageRate >= 0.05.
 *       malware-page-reached: secondStage === 'malware_download' AND
 *           secondStageRate >= 0.03.
 *       no-reports: sent >= 30 AND reported === 0.
 *
 * Postgres mapping:
 *   - server-side computation on sim publish + nightly recompute:
 *     `sim_flags` (sim_id, criterion_id, triggered, why_text,
 *     remediation_action, computed_at).
 *   - dismissals -> `sim_flag_dismissals` (user_id, sim_id, dismissed_at).
 *
 * New data points: sim.secondStage enum, sim.tags JSON, sim.difficulty
 * column. The DIFFICULTY_THRESHOLDS / TAG_PENALTY tables should become
 * tenancy-configurable so customers can tune flag sensitivity.
 *
 * Integration hook: `useFlaggedSimsQuery()` returning
 * { flagged, dismissed } (cache 30 min) plus
 * `useDismissFlaggedSimMutation`.
 */
import React, { useMemo, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Box, Typography, Grid, Button, Snackbar, Alert } from '@mui/material';
import FlagRoundedIcon from '@mui/icons-material/FlagRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import UndoRoundedIcon from '@mui/icons-material/UndoRounded';
import { motion, AnimatePresence } from 'framer-motion';
import MainCard from '../../../components/cards/MainCard';
import { SIMS, SEND_LOG } from '../data';
import useDashboardData from '../hooks/useDashboardData';
import { useGetFlaggedSimAcksQuery, useSaveFlaggedSimAcksMutation } from '../../../store/services/reportLayout.service';

const fadeUp = { hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: 'easeOut' } } };
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.07 } } };

// Flagging is grounded in two things about the sim itself: how hard it is to
// spot, and what call-to-actions (CTAs / tags) it uses. A click rate that's
// fine for a hard sim is alarming on an easy one; a credential-harvest
// template flags at a much lower second-stage rate than a benign one.
//
// Difficulty baseline = "click rate above this is unexpected for that
// difficulty alone". Each high-stakes tag tightens the baseline because the
// CTA is doing more of the work, so a higher click rate implicates the
// message rather than the user.
const DIFFICULTY_THRESHOLDS = {
    1: 0.15, // easy - users should be catching these
    2: 0.30, // medium
    3: 0.50, // hard
};

const TAG_PENALTY = {
    'monetary request':   0.05,
    'urgency':            0.05,
    'implied authority':  0.03,
    'social engineering': 0.02,
};

const TAG_LABEL = {
    'monetary request':   'Monetary request',
    'urgency':            'Urgency',
    'implied authority':  'Implied authority',
    'social engineering': 'Social engineering',
};

// Difficulty + CTA-aware click threshold. The harder the sim, the higher the
// expected click rate; the more high-stakes CTAs it stacks, the lower the
// threshold we'll tolerate before flagging.
function clickThreshold(sim) {
    const base = DIFFICULTY_THRESHOLDS[sim.diff] ?? 0.30;
    const tagPenalty = (sim.tags || []).reduce((p, t) => p + (TAG_PENALTY[t] || 0), 0);
    // Cap the tightening so a heavily-tagged easy sim doesn't drop below 5%.
    return Math.max(0.05, base - tagPenalty);
}

// Trigger criteria. Each criterion is grounded in what the sim is rather than
// a fixed threshold.
const CRITERIA = [
    {
        id: 'click-rate-exceeds-expected',
        label: 'Click rate exceeds expected for this template',
        test: (s, c) => c.clickRate > clickThreshold(s),
        why: (s, c) => {
            const diffLabel = s.diff === 1 ? 'easy' : s.diff === 2 ? 'medium' : 'hard';
            const tagBits = (s.tags || []).filter((t) => TAG_PENALTY[t]).map((t) => TAG_LABEL[t] || t);
            const tagText = tagBits.length ? ` with ${tagBits.join(' + ')}` : '';
            return `Click rate ${Math.round(c.clickRate * 100)}% on a ${diffLabel} sim${tagText} - threshold for this template is ${Math.round(clickThreshold(s) * 100)}%.`;
        },
    },
    {
        id: 'credential-page-reached',
        label: 'Users reached the credential entry page',
        test: (s, c) => s.secondStage === 'credential_harvest' && c.secondStageRate >= 0.05,
        why: (s, c) => `Credential-harvest template - ${Math.round(c.secondStageRate * 100)}% of recipients clicked through to the credential entry page (we record the click, not what was typed).`,
    },
    {
        id: 'malware-page-reached',
        label: 'Users reached the malware download page',
        test: (s, c) => s.secondStage === 'malware_download' && c.secondStageRate >= 0.03,
        why: (s, c) => `Malware-download template - ${Math.round(c.secondStageRate * 100)}% of recipients clicked through to the download page.`,
    },
    {
        id: 'no-reports',
        label: 'Zero reports across 30+ sends',
        test: (s, c) => c.sent >= 30 && c.reported === 0,
        why: (s, c) => `${c.sent} users received this sim and none reported it.`,
    },
];

function statsFor(simName) {
    let sent = 0, clicked = 0, secondStage = 0, reported = 0;
    for (const e of SEND_LOG) {
        if (e.simName !== simName) continue;
        sent++;
        if (e.status === 'clicked' || e.status === 'clicked_2nd') clicked++;
        if (e.status === 'clicked_2nd') secondStage++;
        if (e.status === 'reported') reported++;
    }
    return {
        sent,
        clicked,
        secondStage,
        reported,
        clickRate: sent ? clicked / sent : 0,
        secondStageRate: sent ? secondStage / sent : 0,
    };
}

function HeroStat({ label, value }) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography sx={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.disabled' }}>
                {label}
            </Typography>
            <Typography sx={{ fontSize: 20, fontWeight: 800, color: 'text.primary', letterSpacing: '-0.02em', lineHeight: 1, mt: 0.25 }}>
                {value}
            </Typography>
        </Box>
    );
}

function ProfilePill({ label, tone }) {
    const danger = tone === 'danger';
    return (
        <Box sx={{
            display: 'inline-flex', alignItems: 'center',
            px: '8px', py: '2px',
            borderRadius: '999px',
            fontSize: 10, fontWeight: 700,
            letterSpacing: '0.02em',
            bgcolor: danger ? 'rgba(255,82,82,.10)' : 'background.paper',
            color: danger ? '#E53935' : 'text.secondary',
            border: '1px solid',
            borderColor: danger ? 'rgba(255,82,82,.25)' : 'divider',
        }}>
            {label}
        </Box>
    );
}

function StatPill({ label, value, color }) {
    return (
        <Box sx={{ flex: 1, minWidth: 84, px: 1.5, py: 1, border: '1px solid', borderColor: 'divider', borderRadius: '6px', bgcolor: 'background.paper' }}>
            <Typography sx={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.disabled', mb: 0.25 }}>{label}</Typography>
            <Typography sx={{ fontSize: 16, fontWeight: 800, color: color || 'text.primary', letterSpacing: '-0.02em' }}>{value}</Typography>
        </Box>
    );
}

// Derives the flagged-sim list from the trigger CRITERIA. Exported so the Sims
// page "Flagged Sims" KPI can show the same count this page does — there is no
// server-side flagged field (sim.flag is always false on the platform). Pass the
// server per-sim stats map when available; otherwise it falls back to statsFor()
// over SEND_LOG (period-independent, matching this page's own fallback).
export function computeFlaggedSims(simMap) {
    return SIMS
        .map((s) => {
            const c = (simMap && simMap[s.name]) || statsFor(s.name);
            const triggers = CRITERIA.filter((cr) => cr.test(s, c));
            return { sim: s, counts: c, triggers };
        })
        .filter((f) => f.triggers.length > 0 || f.sim.flag);
}

export default function FlaggedSimsPage({ drill }) {
    const simsQ = useDashboardData({ metric: 'simulations', aggregation: 'sims' });
    const simMap = useMemo(() => {
        if (!Array.isArray(simsQ.data?.sims)) return null;
        const m = {};
        for (const r of simsQ.data.sims) m[r.simName] = { sent: r.sent, clicked: r.clicks, secondStage: r.landings, reported: r.reports, clickRate: r.sent ? r.clicks / r.sent : 0, secondStageRate: r.sent ? r.landings / r.sent : 0 };
        return m;
    }, [simsQ.data]);
    const allFlagged = useMemo(() => computeFlaggedSims(simMap), [simMap]);

    // Dismissed map keyed by sim name -> { at: iso }. Tenancy-wide and
    // server-side (FLAGGED_SIM_ACKS in the reports-b config store), so a flag
    // one admin acknowledges reads as acknowledged for the whole team - and
    // the Flagged Sims KPI on the Simulations page shares the same cache.
    // Local state gives an instant echo; each change PUTs the whole map.
    const rbTenancyId = Number(useSelector((state) => state.tenancy.tenancyId));
    const acksQ = useGetFlaggedSimAcksQuery(rbTenancyId, { skip: !Number.isFinite(rbTenancyId) || rbTenancyId <= 0 });
    const [saveFlaggedSimAcks] = useSaveFlaggedSimAcksMutation();
    const [dismissed, setDismissedLocal] = useState({});
    useEffect(() => { if (acksQ.data?.acks) setDismissedLocal(acksQ.data.acks); }, [acksQ.data]);
    const persistDismissed = (next) => {
        setDismissedLocal(next);
        if (Number.isFinite(rbTenancyId) && rbTenancyId > 0) saveFlaggedSimAcks({ tenancyId: rbTenancyId, acks: next });
    };
    const [showDismissed, setShowDismissed] = useState(false);
    const [toast, setToast] = useState(null);
    const [selectedSim, setSelectedSim] = useState(null);

    const activeFlagged   = allFlagged.filter((f) => !dismissed[f.sim.name]);
    const dismissedItems  = allFlagged.filter((f) => !!dismissed[f.sim.name]);
    const visibleFlagged  = showDismissed ? allFlagged : activeFlagged;

    // Default the selection to the first visible item; reselect when the
    // current selection drops out of the list (dismissed, view toggled).
    useEffect(() => {
        if (visibleFlagged.length === 0) { setSelectedSim(null); return; }
        if (!selectedSim || !visibleFlagged.some((f) => f.sim.name === selectedSim)) {
            setSelectedSim(visibleFlagged[0].sim.name);
        }
    }, [visibleFlagged, selectedSim]);

    const selected = visibleFlagged.find((f) => f.sim.name === selectedSim) || null;

    const dismiss = (simName) => {
        persistDismissed({ ...dismissed, [simName]: { at: new Date().toISOString() } });
        setToast({ severity: 'info', text: `${simName} dismissed.` });
    };
    const restore = (simName) => {
        const next = { ...dismissed };
        delete next[simName];
        persistDismissed(next);
    };

    return (
        <motion.div variants={stagger} initial="hidden" animate="visible" style={{ padding: '6px 24px 48px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {/* Hero banner, the headline number lives here. Red-tinted
                surface signals severity at a glance without needing the
                reader to count flagged cards below. The right side carries
                a derived "severity" reading so the same look works whether
                the customer has 1 flagged sim or 30. */}
            <motion.div variants={fadeUp}>
                {(() => {
                    const total = SIMS.length;
                    const count = activeFlagged.length;
                    const pct = total === 0 ? 0 : Math.round((count / total) * 100);
                    const severity = count === 0 ? 'All clear' : count <= 2 ? 'Watch' : count <= 5 ? 'Action needed' : 'Critical';
                    const severityColor = count === 0 ? '#66BB6A' : count <= 2 ? '#FFCA28' : '#FF5252';
                    const triggerCount = activeFlagged.reduce((s, f) => s + f.triggers.length, 0);
                    return (
                        <Box sx={{
                            position: 'relative',
                            overflow: 'hidden',
                            borderRadius: '14px',
                            border: '1px solid',
                            borderColor: count === 0 ? 'rgba(102,187,106,.25)' : 'rgba(255,82,82,.28)',
                            background: count === 0
                                ? 'linear-gradient(135deg, rgba(102,187,106,.10) 0%, rgba(102,187,106,.02) 60%)'
                                : 'linear-gradient(135deg, rgba(255,82,82,.14) 0%, rgba(255,110,64,.06) 50%, rgba(255,82,82,.02) 100%)',
                            p: { xs: 2.5, md: 3.5 },
                        }}>
                            {/* Decorative flag glyphs in the corner */}
                            <Box aria-hidden sx={{
                                position: 'absolute',
                                top: -30, right: -30,
                                width: 220, height: 220,
                                opacity: 0.10,
                                color: count === 0 ? '#66BB6A' : '#FF5252',
                                pointerEvents: 'none',
                            }}>
                                <FlagRoundedIcon sx={{ fontSize: 220 }} />
                            </Box>
                            <Box aria-hidden sx={{
                                position: 'absolute',
                                bottom: -60, right: 90,
                                width: 160, height: 160,
                                opacity: 0.06,
                                color: count === 0 ? '#66BB6A' : '#FF5252',
                                pointerEvents: 'none',
                                transform: 'rotate(-12deg)',
                            }}>
                                <FlagRoundedIcon sx={{ fontSize: 160 }} />
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', md: 'center' }, gap: { xs: 2, md: 4 }, flexDirection: { xs: 'column', md: 'row' }, position: 'relative' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Box sx={{
                                        width: 56, height: 56,
                                        borderRadius: '14px',
                                        bgcolor: count === 0 ? 'rgba(102,187,106,.20)' : 'rgba(255,82,82,.20)',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        flexShrink: 0,
                                        boxShadow: count === 0
                                            ? '0 0 0 1px rgba(102,187,106,.25), 0 6px 16px rgba(102,187,106,.18)'
                                            : '0 0 0 1px rgba(255,82,82,.25), 0 6px 16px rgba(255,82,82,.22)',
                                    }}>
                                        <FlagRoundedIcon sx={{ fontSize: 28, color: count === 0 ? '#66BB6A' : '#FF5252' }} />
                                    </Box>
                                    <Box>
                                        <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary' }}>
                                            Flagged simulations
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mt: 0.25 }}>
                                            <Typography sx={{ fontSize: 38, fontWeight: 800, letterSpacing: '-0.04em', color: 'text.primary', lineHeight: 1 }}>
                                                {count}
                                            </Typography>
                                            <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                                                of {total} sims - {pct}%
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>

                                {/* Severity chip + sub-stats */}
                                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                                    <Box sx={{
                                        display: 'inline-flex', alignItems: 'center', gap: 0.75,
                                        px: 1.25, py: 0.5,
                                        borderRadius: '999px',
                                        bgcolor: severityColor,
                                        color: '#fff',
                                        fontSize: 11, fontWeight: 800,
                                        textTransform: 'uppercase', letterSpacing: '0.06em',
                                        boxShadow: '0 2px 8px rgba(0,0,0,.18)',
                                    }}>
                                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#fff' }} />
                                        {severity}
                                    </Box>
                                    {count > 0 && (
                                        <>
                                            <HeroStat label="Triggers fired" value={triggerCount} />
                                            <HeroStat label="Criteria" value={CRITERIA.length} />
                                        </>
                                    )}
                                </Box>
                            </Box>
                        </Box>
                    );
                })()}
            </motion.div>

            <motion.div variants={fadeUp}>
                <MainCard content={false} border={false} boxShadow sx={{ bgcolor: 'background.paper' }}>
                    <Box sx={{ p: 3 }}>
                        <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.primary', mb: 1.25 }}>
                            What counts as flagged
                        </Typography>
                        <Grid container spacing={1.5}>
                            {CRITERIA.map((c) => (
                                <Grid item xs={12} md={6} key={c.id}>
                                    <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
                                        <Box sx={{
                                            flexShrink: 0,
                                            width: 6, height: 6,
                                            borderRadius: '50%',
                                            bgcolor: '#FF5252',
                                            mt: '6px',
                                        }} />
                                        <Box>
                                            <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'text.primary' }}>{c.label}</Typography>
                                        </Box>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                </MainCard>
            </motion.div>

            {/* Inbox toolbar, Dismissed count + restore-all */}
            {dismissedItems.length > 0 && (
                <motion.div variants={fadeUp}>
                    <Box sx={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        px: 2, py: 1, borderRadius: '8px',
                        bgcolor: 'action.hover', border: '1px solid', borderColor: 'divider',
                    }}>
                        <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                            {dismissedItems.length} dismissed or actioned
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                                size="small"
                                onClick={() => setShowDismissed((v) => !v)}
                                sx={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary' }}
                            >
                                {showDismissed ? 'Hide' : 'Show'}
                            </Button>
                            <Button
                                size="small"
                                onClick={() => persistDismissed({})}
                                startIcon={<UndoRoundedIcon sx={{ fontSize: '14px !important' }} />}
                                sx={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'secondary.main' }}
                            >
                                Restore all
                            </Button>
                        </Box>
                    </Box>
                </motion.div>
            )}

            {/* Inbox-style two-column layout: list on the left, detail on the right. */}
            {visibleFlagged.length === 0 ? (
                <motion.div variants={fadeUp}>
                    <MainCard content={false} border={false} boxShadow sx={{ bgcolor: 'background.paper' }}>
                        <Box sx={{ p: 4, textAlign: 'center' }}>
                            <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>
                                {activeFlagged.length === 0 && dismissedItems.length > 0
                                    ? 'All flagged sims have been dismissed.'
                                    : 'No simulations are flagged right now. Good job.'}
                            </Typography>
                        </Box>
                    </MainCard>
                </motion.div>
            ) : (
                <motion.div variants={fadeUp}>
                    <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: 'minmax(260px, 320px) 1fr' },
                        gap: 1.5,
                        alignItems: 'stretch',
                    }}>
                        {/* Inbox list */}
                        <MainCard content={false} border={false} boxShadow sx={{ bgcolor: 'background.paper', overflow: 'hidden' }}>
                            <Box sx={{ px: 2, pt: 1.75, pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                                <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.disabled' }}>
                                    Inbox - {activeFlagged.length} open{showDismissed && dismissedItems.length ? ` - ${dismissedItems.length} dismissed` : ''}
                                </Typography>
                            </Box>
                            <Box sx={{ maxHeight: 540, overflowY: 'auto' }}>
                                <AnimatePresence initial={false}>
                                {visibleFlagged.map(({ sim, counts, triggers }) => {
                                    const state = dismissed[sim.name];
                                    const active = selectedSim === sim.name;
                                    return (
                                        <motion.div
                                            key={sim.name}
                                            layout
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: state ? 0.55 : 1 }}
                                            exit={{ opacity: 0, x: -16, transition: { duration: 0.18 } }}
                                            transition={{ duration: 0.18 }}
                                        >
                                            <Box
                                                onClick={() => setSelectedSim(sim.name)}
                                                sx={{
                                                    px: 2, py: 1.25,
                                                    borderBottom: '1px solid', borderColor: 'divider',
                                                    cursor: 'pointer',
                                                    position: 'relative',
                                                    bgcolor: active ? 'action.selected' : 'transparent',
                                                    borderLeft: '3px solid',
                                                    borderLeftColor: active ? 'secondary.main' : 'transparent',
                                                    transition: 'background 150ms, border-color 150ms',
                                                    '&:hover': { bgcolor: active ? 'action.selected' : 'action.hover' },
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.25 }}>
                                                    <FlagRoundedIcon sx={{ fontSize: 12, color: state ? 'text.disabled' : '#FF5252' }} />
                                                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'text.primary', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        {sim.name}
                                                    </Typography>
                                                    {state && (
                                                        <Typography sx={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.disabled' }}>
                                                            Dismissed
                                                        </Typography>
                                                    )}
                                                </Box>
                                                <Typography sx={{ fontSize: 10, color: 'text.secondary', mb: 0.5 }}>
                                                    {triggers.length} trigger{triggers.length === 1 ? '' : 's'} - {counts.sent} sends - {Math.round(counts.clickRate * 100)}% click
                                                </Typography>
                                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                                    {triggers.slice(0, 2).map((t) => (
                                                        <Box key={t.id} sx={{
                                                            display: 'inline-flex', alignItems: 'center',
                                                            px: '6px', py: '1px',
                                                            borderRadius: '999px',
                                                            fontSize: 9, fontWeight: 700,
                                                            bgcolor: 'rgba(255,82,82,.10)',
                                                            color: '#E53935',
                                                            border: '1px solid rgba(255,82,82,.25)',
                                                        }}>
                                                            {t.label}
                                                        </Box>
                                                    ))}
                                                    {triggers.length > 2 && (
                                                        <Typography sx={{ fontSize: 9, color: 'text.disabled', alignSelf: 'center' }}>
                                                            +{triggers.length - 2}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Box>
                                        </motion.div>
                                    );
                                })}
                                </AnimatePresence>
                            </Box>
                        </MainCard>

                        {/* Detail pane */}
                        <MainCard content={false} border={false} boxShadow sx={{ bgcolor: 'background.paper' }}>
                            {selected ? (() => {
                                const { sim, counts, triggers } = selected;
                                const state = dismissed[sim.name];
                                return (
                                    <Box>
                                        <Box sx={{ px: 3, pt: 2.5, pb: 2, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
                                            <Box
                                                onClick={() => drill('sim-detail', sim.name, { type: 'sim', value: sim.name })}
                                                sx={{ cursor: 'pointer', '&:hover .name': { color: 'secondary.main' } }}
                                            >
                                                <Typography className="name" sx={{ fontSize: 14, fontWeight: 700, color: 'text.primary', transition: 'color .15s' }}>
                                                    {sim.name}
                                                </Typography>
                                                <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                                                    Difficulty {sim.diff}/3 - {counts.sent} sends in window
                                                </Typography>
                                            </Box>
                                            {state ? (
                                                <Button
                                                    size="small"
                                                    onClick={() => restore(sim.name)}
                                                    startIcon={<UndoRoundedIcon sx={{ fontSize: '14px !important' }} />}
                                                    sx={{
                                                        fontSize: 11, fontWeight: 600, textTransform: 'none',
                                                        color: 'text.secondary',
                                                        border: '1px solid', borderColor: 'divider',
                                                        borderRadius: '6px', px: 1.25, py: '4px',
                                                        '&:hover': { color: 'text.primary', borderColor: 'text.disabled' },
                                                    }}
                                                >
                                                    Restore
                                                </Button>
                                            ) : (
                                                <Button
                                                    size="small"
                                                    onClick={() => dismiss(sim.name)}
                                                    startIcon={<CloseRoundedIcon sx={{ fontSize: '14px !important' }} />}
                                                    sx={{
                                                        fontSize: 11, fontWeight: 600, textTransform: 'none',
                                                        color: 'text.secondary', border: '1px solid', borderColor: 'divider',
                                                        borderRadius: '6px', px: 1.25, py: '4px',
                                                        '&:hover': { color: 'text.primary', borderColor: 'text.disabled' },
                                                    }}
                                                >
                                                    Dismiss
                                                </Button>
                                            )}
                                        </Box>

                                        {/* Template profile: difficulty, second-stage type, CTAs, computed threshold */}
                                        <Box sx={{ px: 3, py: 1.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'action.hover' }}>
                                            <Typography sx={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.disabled' }}>
                                                Template
                                            </Typography>
                                            <ProfilePill label={`${sim.diff === 1 ? 'Easy' : sim.diff === 2 ? 'Medium' : 'Hard'} (${sim.diff}/3)`} />
                                            {sim.secondStage && sim.secondStage !== 'none' && (
                                                <ProfilePill label={sim.secondStage === 'credential_harvest' ? 'Credential harvest' : 'Malware download'} tone="danger" />
                                            )}
                                            {(sim.tags || []).filter((t) => TAG_LABEL[t]).map((t) => (
                                                <ProfilePill key={t} label={TAG_LABEL[t]} />
                                            ))}
                                            <Box sx={{ flex: 1 }} />
                                            <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>
                                                Flag threshold for this template: <strong style={{ color: 'inherit' }}>{Math.round(clickThreshold(sim) * 100)}%</strong> click
                                            </Typography>
                                        </Box>

                                        <Box sx={{ px: 3, py: 2, display: 'flex', gap: 1, flexWrap: 'wrap', borderBottom: '1px solid', borderColor: 'divider' }}>
                                            <StatPill label="Sends"        value={counts.sent} />
                                            <StatPill label="Click rate"   value={`${Math.round(counts.clickRate * 100)}%`} color={counts.clickRate > clickThreshold(sim) ? '#FF5252' : 'text.primary'} />
                                            <StatPill label="2nd stage"    value={counts.secondStage} color={counts.secondStage > 0 ? '#FF5252' : 'text.primary'} />
                                            <StatPill label="Reports"      value={counts.reported} color={counts.reported > 0 ? '#66BB6A' : 'text.disabled'} />
                                        </Box>

                                        <Box sx={{ px: 3, py: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                            {triggers.map((t) => (
                                                <Box key={t.id} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                                                    <Box sx={{
                                                        flexShrink: 0, mt: '4px',
                                                        width: 14, height: 14, borderRadius: '50%',
                                                        bgcolor: '#FF5252', color: '#fff',
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                        fontSize: 9, fontWeight: 800,
                                                    }}>!</Box>
                                                    <Box sx={{ flex: 1 }}>
                                                        <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'text.primary' }}>
                                                            {t.label}
                                                        </Typography>
                                                        <Typography sx={{ fontSize: 11, color: 'text.secondary', lineHeight: 1.55 }}>
                                                            {t.why(sim, counts)}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            ))}
                                        </Box>
                                    </Box>
                                );
                            })() : (
                                <Box sx={{ p: 4, textAlign: 'center' }}>
                                    <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>Select a flagged simulation from the list to see the detail.</Typography>
                                </Box>
                            )}
                        </MainCard>
                    </Box>
                </motion.div>
            )}
            <Snackbar
                open={!!toast}
                autoHideDuration={2600}
                onClose={() => setToast(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                {toast ? (
                    <Alert
                        severity={toast.severity}
                        variant="filled"
                        onClose={() => setToast(null)}
                        sx={{ fontSize: 12, alignItems: 'center' }}
                    >
                        {toast.text}
                    </Alert>
                ) : undefined}
            </Snackbar>
        </motion.div>
    );
}
